/**
 * A etiqueta em si e a folha que a leva ao papel.
 *
 * As duas moram no mesmo arquivo porque são a mesma decisão vista de duas
 * distâncias: o cartão é o que sai colado na peça, a folha é como ele se
 * repete na A4. Separá-las convidaria a prévia e a impressão a divergirem —
 * e uma prévia que mente é pior que não ter prévia, porque a divergência só
 * aparece depois de a folha de adesivo ter sido gasta.
 *
 * Tudo é medido em MILÍMETRO e em PONTO, e não em pixel: milímetro é a
 * unidade do papel, e o pixel do CSS depende do DPI que a impressora
 * resolver usar.
 */
import { formatarMoeda } from '../../plataforma/dinheiro'
import type { LinhaSKU } from '../../plataforma/produtos'
import { CodigoDeBarras } from './CodigoDeBarras'
import {
  ALTURA_A4_MM,
  LARGURA_A4_MM,
  MARGEM_FOLHA_MM,
  medidaDaEtiqueta,
  type Conteudo,
  type Etiqueta,
  type Folha,
} from './etiquetas'

/**
 * Uma etiqueta desenhada no tamanho real.
 *
 * `#000000` em vez do token de tinta, de propósito: o token existe para a
 * interface, que tem contraste calculado para monitor. Etiqueta é preto sobre
 * branco, porque é o que a impressora térmica e a laser sabem fazer, e o que
 * o leitor espera enxergar.
 */
export function CartaoDaEtiqueta({
  sku,
  conteudo,
  nomeDaLoja,
  folha,
  comBorda = false,
}: {
  sku: LinhaSKU
  conteudo: Conteudo
  nomeDaLoja: string
  folha: Folha
  /** Fio de contorno — ajuda a conferir o recorte na prévia e no papel comum. */
  comBorda?: boolean
}) {
  const medida = medidaDaEtiqueta(folha)

  return (
    <div
      style={{
        width: `${medida.largura}mm`,
        height: `${medida.altura}mm`,
        padding: '2mm',
        color: '#000000',
        background: '#FFFFFF',
        border: comBorda ? '0.2mm dashed #B9BEC7' : 'none',
        display: 'flex',
        flexDirection: 'column',
        gap: '0.6mm',
        overflow: 'hidden',
        breakInside: 'avoid',
      }}
    >
      {conteudo.logo && nomeDaLoja && (
        <div className="font-titulo truncate text-[5pt] leading-none font-bold tracking-[0.06em] uppercase">
          {nomeDaLoja}
        </div>
      )}

      {conteudo.nome && (
        <div className="truncate text-[7.5pt] leading-tight font-bold">{sku.produto}</div>
      )}

      {conteudo.variacao && (
        <div className="truncate text-[6.5pt] leading-none">
          {sku.cor} · {sku.tamanho}
        </div>
      )}

      {/* Empurra preço e barras para a base: com dois campos ou com seis, a
          âncora da leitura é sempre o rodapé da etiqueta. */}
      <div style={{ flex: 1 }} />

      {conteudo.preco && (
        <div className="tabular text-[11pt] leading-none font-bold">
          {formatarMoeda(sku.preco_centavos)}
        </div>
      )}

      {conteudo.barras && (
        <CodigoDeBarras codigo={sku.codigo_barras} alturaMM={Math.min(8, medida.altura / 4)} />
      )}

      {conteudo.codigo && (
        <div className="tabular truncate text-[5.5pt] leading-none">{sku.codigo_legivel}</div>
      )}
    </div>
  )
}

/**
 * A pilha de folhas A4, montada no portal de impressão.
 *
 * `hidden print:block` é o mesmo par de `RelatorioImpressao`: em tela o
 * elemento não existe visualmente, e no papel ele é a única coisa que sobra,
 * porque o `tokens.css` esconde `#raiz` quando encontra
 * `#area-de-impressao-lumma` no `<body>`.
 */
export function FolhaDeEtiquetas({
  paginas,
  folha,
  conteudo,
  nomeDaLoja,
  comBorda,
}: {
  paginas: (Etiqueta | null)[][]
  folha: Folha
  conteudo: Conteudo
  nomeDaLoja: string
  comBorda: boolean
}) {
  const medida = medidaDaEtiqueta(folha)

  return (
    <div className="hidden bg-white print:block">
      {/*
        `@page` não sai do Tailwind e não aceita `:has()`, então a regra viaja
        aqui dentro — e some junto com o portal quando a impressão termina. Uma
        regra global de página em `tokens.css` mudaria a margem de TODA
        impressão do sistema, inclusive a do relatório de listagem, que já tem
        a margem dele no próprio conteúdo.
      */}
      <style>{`@page { size: A4 portrait; margin: ${MARGEM_FOLHA_MM}mm; }`}</style>

      {paginas.map((pagina, indice) => (
        <div
          key={indice}
          style={{
            width: `${LARGURA_A4_MM - 2 * MARGEM_FOLHA_MM}mm`,
            height: `${ALTURA_A4_MM - 2 * MARGEM_FOLHA_MM}mm`,
            display: 'grid',
            gridTemplateColumns: `repeat(${folha.colunas}, ${medida.largura}mm)`,
            gridAutoRows: `${medida.altura}mm`,
            // A última folha não força quebra: uma página em branco no fim é
            // uma folha de adesivo pela metade no lixo.
            breakAfter: indice === paginas.length - 1 ? 'auto' : 'page',
          }}
        >
          {pagina.map((etiqueta, posicao) =>
            etiqueta ? (
              <CartaoDaEtiqueta
                key={etiqueta.chave}
                sku={etiqueta.sku}
                conteudo={conteudo}
                nomeDaLoja={nomeDaLoja}
                folha={folha}
                comBorda={comBorda}
              />
            ) : (
              // A posição pulada precisa OCUPAR espaço, não desaparecer: é ela
              // que empurra a primeira etiqueta para a posição livre da folha
              // já usada.
              <div key={`vazia-${posicao}`} aria-hidden />
            ),
          )}
        </div>
      ))}
    </div>
  )
}
